import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { LayerApi } from '../../../api/layer-api';
import { IMCMapView } from '../../../classes/mcMapView';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { ThemeService } from '../../../services/theme.service';
import { IMapcoreGroupLayer, IMapcoreMapImageLayer, IMapcoreSubLayer, isClassBreakInfo, isClassBreaksRenderer, isDynamicDataLayerSource, isGeometryDataSource, isMapImageLayer, isSimpleRenderer, isSymbolPropertiesWithOutline, isUniqueValueRenderer } from '../../../types';
import { BaseComponent } from '../../base.component';

/* tslint:disable:cyclomatic-complexity */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-theme-editor',
    styleUrls: ['../../sharedStyles/base.scss', './theme-editor.component.scss'],
    templateUrl: './theme-editor.component.html'
})
export class ThemeEditorComponent extends BaseComponent implements AfterViewInit {
    private _layerId: string;
    @Input() mapViewRef: IMCMapView;
    @Input() set layerId(value) {
        this._layerId = value;
    }
    get layerId() {
        return this._layerId;
    }
    @ViewChild('content') content: ElementRef<HTMLDivElement>;
    @ViewChild('slider') sliderContainer: ElementRef<HTMLDivElement>;
    @ViewChild('fieldSelect') fieldSelect: ElementRef<HTMLSelectElement>;
    @ViewChild('classes') classes: ElementRef<HTMLInputElement>;
    windowRef: any;
    layer: IMapcoreMapImageLayer;
    currentSubLayer: IMapcoreSubLayer;
    layerData;
    layerColumns: { name: string; alias: string }[];
    slider: __esri.ClassedColorSlider;
    Histogram: __esri.histogram;
    ClassedColorSlider: __esri.ClassedColorSliderConstructor;
    renderers: (__esri.UniqueValueRenderer | __esri.ClassBreaksRenderer)[] = [];
    classBreakInfos: (__esri.ClassBreakInfo | __esri.UniqueValueInfo)[];
    histogramResult: __esri.HistogramResult;
    errorMessage: string;
    loadingData = false;
    numSelectedClasses: number;
    newColorScheme: __esri.ColorSchemes;
    currentColors: __esri.Color[] = [];
    classBreakResult: any;
    sliderContainerDiv;
    colorPickerClassNo = null;
    tempColor;
    defaultNumClasses = 5;
    minClasses: number;
    maxClasses: number;
    validationState: ValidationState = 'fresh';
    classBreakChange: Subject<string> = new Subject<string>();
    forceUniqueValues = false;
    isNumericData = false;
    isDate = false;
    polygonSymbol: __esri.SimpleFillSymbol;
    symbol: __esri.Symbol[];
    keepThemeOnOriginalLayer = false;
    selectedField: string;

    @Input()
    @InputConverter()
    useEsriScheme: boolean = false;

    @Input()
    @InputConverter()
    useSymbolScheme: boolean = false;

    classInputSub: Subscription;

    constructor(_vcr: ViewContainerRef,
                private readonly layerService: LayerService,
                private readonly layerApi: LayerApi,
                private readonly cdr: ChangeDetectorRef,
                private readonly themeService: ThemeService) {
        super(_vcr, true);
        this.minClasses = themeService.minMaxes.range.min;
        this.maxClasses = themeService.minMaxes.range.max;
        this.getClosestAncestor('mc-window').then((window: any) => {
            if (window) {
                this.windowRef = window;
                this.windowRef.addEventListener('openChange', open => {
                    if (open.detail) {
                        this.windowOpened();
                    } else {
                        this.destroySlider();
                        this.destroyComponent();
                    }
                });
            } else {
                this.windowOpened();
            }
        });
    }

    watchClassBreaks() {
        this.classInputSub = this.classBreakChange.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => this.generateRenderer());
    }

    changeClassBreaks(event: Event) {
        this.classBreakChange.next((event.currentTarget as any).value);
    }

    async ngAfterViewInit() {
        this.mapViewRef = this.mapViewRef ? this.mapViewRef : await this.setupMapViewRef(this.mapViewRef);
        this.themeService.mapView = this.mapViewRef;
        this.isReady();
    }

    async windowOpened() {
        [
            this.Histogram,
            this.ClassedColorSlider
        ] = await Promise.all([
            ESRI.Histogram,
            ESRI.ClassedColorSlider
        ]);
        const layer = this.layerService.getEsriLayerByUniqueId(this.layerId);
        if (isMapImageLayer(layer)) {
            this.layer = layer;
            this.layerColumns = this.themeService.getLayerColumnsFromLayer(this.layer);
            this.currentSubLayer = this.layer.sublayers.getItemAt(0);
            this.currentColors = [];
            const renderer = this.currentSubLayer.renderer;
            if (isSimpleRenderer(renderer)) {
                this.classes.nativeElement.value = this.defaultNumClasses.toString();
                this.symbol = [renderer.symbol];
                this.selectedField = this.layerColumns[0].name;
                this.layer.mapcoreProps.simpleRenderers = this.layerService.getRendererDefinitionsForLayer(this.layer);
            } else if (isClassBreaksRenderer(renderer)) {
                this.keepThemeOnOriginalLayer = true;
                this.classes.nativeElement.value = renderer.classBreakInfos.length.toString();
                this.symbol = renderer.classBreakInfos.map(classBreak => classBreak.symbol);
                this.selectedField = renderer.field;
            } else if (isUniqueValueRenderer(renderer)) {
                this.keepThemeOnOriginalLayer = true;
                this.classes.nativeElement.value = renderer.uniqueValueInfos.length.toString();
                this.symbol = renderer.uniqueValueInfos.map(classBreak => classBreak.symbol);
                this.selectedField = renderer.field;
            }
            this.watchClassBreaks();
            await this.triggerRenderer();
        } else {
            throw new Error('Unsupported layer type for Theme Editor');
        }
    }

    async triggerRenderer() {
        if (isUniqueValueRenderer(this.currentSubLayer.renderer) || isClassBreaksRenderer(this.currentSubLayer.renderer)) {
            await this.generateRendererFromThemeLayer();
        } else {
            this.resetValidation();
            await this.generateRenderer();
        }
    }

    async forceUpdateWithUniqueValues(forceUniqueValues: boolean) {
        this.forceUniqueValues = forceUniqueValues;
        this.updateMinMax();
        await this.generateRenderer();
    }

    resetValidation() {
        this.validationState = 'fresh';
    }

    private get minError() { return `This field has fewer than ${this.minClasses} class breaks. Please try changing the layer filter or selecting a different field.`; }
    private get maxError() { return `This field has more than ${this.maxClasses} class breaks. Please try filtering the layer or selecting a different field.`; }

    get uniqueValueInfoRenderers() {
        return this.renderers.filter(isUniqueValueRenderer) as __esri.UniqueValueRenderer[];
    }
    get classBreakInfoRenderers() {
        return this.renderers.filter(isClassBreaksRenderer) as __esri.ClassBreaksRenderer[];
    }

    private setInfos(infos: (__esri.UniqueValueInfo | __esri.ClassBreakInfo)[]) {
        this.classBreakInfos = [];
        this.numSelectedClasses = infos.length;
        this.classes.nativeElement.value = this.numSelectedClasses.toString();
        for (const info of infos) {
            this.classBreakInfos.push(info);
        }
    }

    async generateRendererFromThemeLayer() {
        this.errorMessage = null;
        this.loadingData = true;

        if ((isUniqueValueRenderer(this.currentSubLayer.renderer) || isClassBreaksRenderer(this.currentSubLayer.renderer))
            && isDynamicDataLayerSource(this.currentSubLayer.source) && isGeometryDataSource(this.currentSubLayer.source.dataSource)) {

            this.selectedField = this.currentSubLayer.renderer.field;
            this.forceUniqueValues = isUniqueValueRenderer(this.currentSubLayer.renderer);
            this.updateMinMax();
            this.setInfos(isUniqueValueRenderer(this.currentSubLayer.renderer) ? this.currentSubLayer.renderer.uniqueValueInfos : this.currentSubLayer.renderer.classBreakInfos);
            const recordCount = await this.layerApi.getLayerRecordCount(this.layer.mapcoreProps.id || this.layer.mapcoreProps.parentId, this.layer.mapcoreProps.query);
            if (recordCount && recordCount.length && recordCount[0].recordCount > 0) {
                try {
                    this.isDate = false;
                    const selectedColumn = this.layer.mapcoreProps.columns.filter(x => x.name === this.selectedField);
                    if (selectedColumn && selectedColumn.length && selectedColumn.length > 0 && selectedColumn[0] && selectedColumn[0].databaseDataType && (selectedColumn[0].databaseDataType.toUpperCase() === 'DATE' || selectedColumn[0].name.toUpperCase().indexOf('DATE') > -1)) {
                        this.isDate = true;
                    }
                    const result = await this.themeService.getLayerClassBreakData(this.layer.mapcoreProps.parentId,
                        this.selectedField,
                        this.numSelectedClasses,
                        this.forceUniqueValues,
                        JSON.parse(this.currentSubLayer.source.dataSource.query),
                        this.isDate);
                    this.isNumericData = result.IsNumericData;
                    if (result.ClassBreakInfos.length < this.minClasses) {
                        this.errorMessage = this.minError;
                    } else if (result.ClassBreakInfos.length > this.maxClasses) {
                        this.errorMessage = this.maxError;
                    } else if (result.ClassBreakInfos.length < this.minClasses && this.isNumericData && !this.forceUniqueValues) {
                        await this.forceUpdateWithUniqueValues(true);
                    } else {
                        const symbols = await this.themeService.getSimpleLayerSymbols(this.layer);
                        this.currentColors = this.themeService.getSublayerColors(this.layer.sublayers.getItemAt(0));
                        const colorSets = await this.getColorSets(result.ClassBreakInfos.length, symbols, this.currentColors);
                        this.histogramResult = result.Histogram;
                        if ((this.isNumericData && !this.forceUniqueValues) && isClassBreakInfo(this.classBreakInfos)) {

                            const minValue = result.ClassBreakInfos[0].minValue;
                            const maxValue = result.ClassBreakInfos[result.ClassBreakInfos.length - 1].maxValue;
                            this.classBreakInfos[0].minValue = minValue;
                            this.classBreakInfos[this.classBreakInfos.length - 1].maxValue = maxValue;
                            this.renderers = await this.themeService.buildNumericRenderer(this.layer, this.selectedField, this.classBreakInfos, symbols, colorSets);
                            this.classBreakResult = {
                                classBreakInfos: this.classBreakInfos,
                                minValue,
                                maxValue,
                                normalizationTotal: undefined
                            };
                            this.updateColorSlider(this.renderers[0], this.classBreakResult, this.histogramResult, this.newColorScheme);
                            this.appendFieldToLayerName(this.layer);
                        } else {
                            this.renderers = await this.themeService.buildUniqueValueRenderer(this.layer, this.selectedField, <__esri.ClassBreakInfo[]>this.classBreakInfos, symbols, colorSets);
                            this.appendFieldToLayerName(this.layer);
                            this.classes.nativeElement.disabled = true;
                            this.layerService.mapLayersSub.next();
                        }
                        this.layer.mapcoreProps.isThemed = true;
                    }
                }
                catch (err) {
                    this.errorMessage = 'There was an error loading data, please try again or contact an administrator.';
                }
            } else {
                this.errorMessage = 'No records for the current layer. Theme can\'t be applied.';
            }
        } else {
            this.errorMessage = 'Error restoring theme layer properties.';
        }
        this.loadingData = false;
        this.cdr.detectChanges();
    }

    async generateRenderer(columnChange: boolean = false) {
        if (columnChange) {
            this.forceUniqueValues = false;
            this.resetValidation();
            this.updateMinMax();
        }
        this.errorMessage = null;
        this.loadingData = true;
        const recordCount = await this.layerApi.getLayerRecordCount(this.layer.mapcoreProps.id || this.layer.mapcoreProps.parentId, this.layer.mapcoreProps.query);
        if (recordCount && recordCount.length && recordCount[0].recordCount > 0
            && isDynamicDataLayerSource(this.currentSubLayer.source) && isGeometryDataSource(this.currentSubLayer.source.dataSource)) {
            this.numSelectedClasses = parseInt(this.classes.nativeElement.value, 10);
            this.colorPickerClassNo = null;
            this.renderers = [];
            this.destroySlider();
            try {
                this.isDate = false;
                const selectedColumn = this.layer.mapcoreProps.columns.filter(x => x.name === this.selectedField);
                if (selectedColumn && selectedColumn.length && selectedColumn.length > 0 && selectedColumn[0] && selectedColumn[0].databaseDataType && (selectedColumn[0].databaseDataType.toUpperCase() === 'DATE' || selectedColumn[0].name.toUpperCase().indexOf('DATE') > -1)) {
                    this.isDate = true;
                }
                const result = await this.themeService.getLayerClassBreakData(this.layer.mapcoreProps.parentId,
                    this.selectedField,
                    this.numSelectedClasses,
                    this.forceUniqueValues,
                    JSON.parse(this.currentSubLayer.source.dataSource.query),
                    this.isDate
                );
                this.isNumericData = result.IsNumericData;
                if (result.ClassBreakInfos.length < this.minClasses || result.ClassBreakInfos.length > this.maxClasses) {
                    const errorMessage = result.ClassBreakInfos.length < this.minClasses ? this.minError : this.maxError;

                    if (this.validationState === 'fresh') {
                        if (this.forceUniqueValues) {
                            this.validationState = 'failed';
                            this.errorMessage = errorMessage;
                            this.isNumericData = result.IsNumericData;
                        } else if (this.isNumericData || this.isDate) {
                            this.validationState = 'numeric_resetNumSelectedClasses';
                            this.classes.nativeElement.value = this.defaultNumClasses.toString();
                            await this.forceUpdateWithUniqueValues(false);
                        } else {
                            this.validationState = 'nonNumeric_forcedUniqueValues';
                            await this.forceUpdateWithUniqueValues(true);
                        }
                    } else {
                        this.errorMessage = errorMessage;
                        this.isNumericData = result.IsNumericData;
                        this.validationState = 'failed';
                    }
                } else {
                    this.classBreakInfos = result.ClassBreakInfos;
                    this.histogramResult = result.Histogram;
                    this.validationState = 'fresh';
                    const symbols = await this.themeService.getSimpleLayerSymbols(this.layer);
                    const colorSets = await this.getColorSets(result.ClassBreakInfos.length, symbols);
                    if (((this.isNumericData || this.isDate) && !this.forceUniqueValues) && isClassBreakInfo(this.classBreakInfos)) {
                        this.renderers = await this.themeService.buildNumericRenderer(this.layer, this.selectedField, result.ClassBreakInfos, symbols, colorSets);
                        this.classBreakResult = {
                            classBreakInfos: this.classBreakInfos,
                            minValue: this.classBreakInfos[0].minValue,
                            maxValue: this.classBreakInfos[this.classBreakInfos.length - 1].maxValue,
                            normalizationTotal: undefined
                        };
                        this.layerService.saveClassBreakLabels(this.layer);
                        this.themeService.saveClassBreakLabels(this.layer);
                        this.updateColorSlider(this.renderers[0], this.classBreakResult, this.histogramResult, this.newColorScheme);
                        this.appendFieldToLayerName(this.layer);
                    } else {
                        this.renderers = await this.themeService.buildUniqueValueRenderer(this.layer, this.selectedField, result.ClassBreakInfos, symbols, colorSets);
                        this.appendFieldToLayerName(this.layer);
                        this.classes.nativeElement.disabled = true;
                        this.layerService.mapLayersSub.next();
                        this.forceUniqueValues = true;
                    }
                    this.layer.mapcoreProps.isThemed = true;
                }
                this.dispatchUpdateEvent();
            } catch (error) {
                this.errorMessage = 'There was an error loading data, please try again or contact an administrator.';
            }
        } else {
            this.errorMessage = 'No records for the current layer. Theme can\'t be applied.';
        }
        this.loadingData = false;
        this.cdr.detectChanges();
    }

    private async getColorSets(classes: number, symbols: __esri.Symbol[], currentColors?: __esri.Color[]): Promise<__esri.Color[][]> {
        const sublayerColors = [];
        // use first sublayers colors but loop in case we change that in the future
        for (const _ of this.layer.sublayers.toArray()) {
            const [colors, scheme] = await this.themeService.getClassColors(classes, currentColors, symbols[0], this.useSymbolScheme, this.useEsriScheme);
            this.newColorScheme = scheme;
            sublayerColors.push(colors);
        }
        this.currentColors = sublayerColors[0];
        return sublayerColors;
    }

    dispatchUpdateEvent() {
        window.dispatchEvent(new CustomEvent<string>('symbolChange', { detail: this.layerId }));
    }

    updateMinMax() {
        if (this.forceUniqueValues) {
            this.minClasses = this.themeService.minMaxes.unique.min;
            this.maxClasses = this.themeService.minMaxes.unique.max;
        } else {
            this.minClasses = this.themeService.minMaxes.range.min;
            this.maxClasses = this.themeService.minMaxes.range.max;
        }
    }

    appendFieldToLayerName(layer: IMapcoreMapImageLayer) {
        const column = this.layerColumns.find(lc => lc.name.toLowerCase() === this.selectedField.toLowerCase());
        if (layer.mapcoreProps.name.match(/(\(Theme Field: ).*?(\))(?!.*\2)/g)) {
            layer.mapcoreProps.name = layer.mapcoreProps.name.replace(/(\(Theme Field: ).*?(\))(?!.*\2)/g, `$1${column.alias}$2`);
        } else {
            layer.mapcoreProps.name = `${layer.mapcoreProps.name} (Theme Field: ${column.alias})`;
        }
    }

    removeFieldFromLayerName(layer: IMapcoreMapImageLayer) {
        if (layer.mapcoreProps.name.match(/(\(Theme Field: ).*?(\))(?!.*\2)/g)) {
            layer.mapcoreProps.name = layer.mapcoreProps.name.replace(/(\(Theme Field: ).*?(\))(?!.*\2)/g, '').trim();
        }
    }

    // If manual classification method is selected, then create
    // a classed color slider to allow user to manually modify
    // the class breaks starting with the generated renderer
    updateColorSlider(renderer: __esri.ClassBreaksRenderer | __esri.UniqueValueRenderer, classBreaksResult: __esri.ClassBreaksResult, histogramResult: __esri.HistogramResult, newColorScheme: __esri.ColorSchemes) {
        const rendererResult = {
            renderer,
            classBreaksResult,
            colorScheme: newColorScheme.primaryScheme,
            defaultValuesUsed: false,
            basemapId: this.mapViewRef.map.basemap.id
        } as __esri.ClassBreaksRendererResult;
        this.sliderContainerDiv = document.createElement('div');
        this.sliderContainerDiv.id = 'sliderContainerDiv';
        this.sliderContainer.nativeElement.appendChild(this.sliderContainerDiv);
        this.slider = this.ClassedColorSlider.fromRendererResult(
            rendererResult,
            histogramResult
        );
        this.slider.container = this.sliderContainerDiv;
        this.slider.viewModel.precision = 1; // TODO: Find a way to check for int vs decimal
        this.changeEventHandler(false);
        this.slider.on('thumb-drag', () => this.changeEventHandler(true));
        this.slider.on('thumb-change', () => this.changeEventHandler(true));
        const observer = new MutationObserver(() => {
            if (this.sliderContainerDiv.contains(this.sliderContainerDiv.getElementsByClassName('esri-slider')[0])) {
                this.sliderContainerDiv.setAttribute('style', 'height: 100%;');
                this.sliderContainerDiv.getElementsByClassName('esri-classed-color-slider__slider-container')[0].setAttribute('style', 'height: 100%');
                const esriSlider = this.sliderContainerDiv.getElementsByClassName('esri-slider')[0];
                esriSlider.setAttribute('style', 'height: 100%');
                observer.disconnect();
            }
        });
        observer.observe(this.sliderContainerDiv, { attributes: false, childList: true, characterData: false, subtree: true });
    }

    changeEventHandler(userChangedClassBreak: boolean) {
        const usingOriginalLayerFilter = this.layer.mapcoreProps.classBreakLabels === this.themeService.getClassBreakLabels(this.layer);
        const themeColors = this.themeService.getColorsForField(this.layer, this.selectedField);

        for (const sublayer of this.layer.sublayers.toArray()) {
            if (isClassBreaksRenderer(sublayer.renderer)) {
                const originalClassBreaks: Array<any> = sublayer.renderer.classBreakInfos;
                const renderer = sublayer.renderer.clone();
                renderer.classBreakInfos = this.slider.updateClassBreakInfos(
                    renderer.classBreakInfos
                );
                sublayer.renderer = renderer;

                for (const [index, classBreak] of renderer.classBreakInfos.entries()) {
                    const originalLabel = originalClassBreaks[index].label;
                    const newLabel = `Break-${classBreak.minValue}`;

                    (<any>classBreak).value = newLabel;
                    (<any>classBreak).label = newLabel;

                    for (const r of this.renderers) {
                        (<any>r).classBreakInfos[index].label = newLabel;
                        (<any>r).classBreakInfos[index].value = newLabel;
                    }

                    if (usingOriginalLayerFilter && originalLabel !== newLabel && originalLabel in themeColors) {
                        // Save the class break's color if its label changes
                        const color = themeColors[originalClassBreaks[index].label];
                        this.themeService.forgetColor(this.layer, this.selectedField, originalClassBreaks[index].label);
                        this.themeService.saveColor(this.layer, this.selectedField, (<any>classBreak).value, color);
                    }
                }

                this.classBreakResult.classBreakInfos = renderer.classBreakInfos;
                for (const r of this.renderers) {
                    if (isClassBreaksRenderer(r)) {
                        for (let i = 0; i < r.classBreakInfos.length; i++) {
                            r.classBreakInfos[i].maxValue = renderer.classBreakInfos[i].maxValue;
                            r.classBreakInfos[i].minValue = renderer.classBreakInfos[i].minValue;
                        }
                    }
                }
            }
        }
        this.layerService.mapLayersSub.next();

        if (userChangedClassBreak && usingOriginalLayerFilter) {
            this.layerService.saveClassBreakLabels(this.layer);
            this.themeService.saveClassBreakLabels(this.layer);
        }
    }

    openClassColorPicker(index, color) {
        this.colorPickerClassNo = index;
        this.tempColor = color;
    }

    updateColorFromPicker(evt, index, valueType) {
        for (const sublayer of this.renderers) {
            const temp = sublayer[valueType];
            temp[index].symbol.color = evt;
            if (temp[index].symbol.style === 'none') {
                temp[index].symbol.outline.color = evt;
            }
            sublayer[valueType] = temp;
        }
        for (const sublayer of this.layer.sublayers.toArray()) {
            sublayer.renderer[valueType][index].symbol.color = evt;
            if (sublayer.renderer[valueType][index].symbol.style === 'none') {
                sublayer.renderer[valueType][index].symbol.outline.color = evt;
            }
        }
        this.layer.refresh();
        if (valueType === 'classBreakInfos') {
            this.destroySlider();
            this.updateColorSlider(this.renderers[0], this.classBreakResult, this.histogramResult, this.newColorScheme);
        } else {
            this.layerService.mapLayersSub.next();
        }
        this.saveCustomColor(evt, index, valueType);
        this.dispatchUpdateEvent();
    }

    cancelColorPicker(index, valueType) {
        for (const sublayer of this.renderers) {
            sublayer[valueType][index].symbol.color = this.tempColor;
        }
        this.colorPickerClassNo = null;
        if (valueType === 'classBreakInfos') {
            this.destroySlider();
            this.updateColorSlider(this.renderers[0], this.classBreakResult, this.histogramResult, this.newColorScheme);
        }
        this.layer.refresh();
    }

    saveCustomColor(color: string, index: number, valueType: string) {
        const infos = this.renderers[0][valueType];
        this.themeService.saveColor(this.layer, this.selectedField, infos[index].value, color);
    }

    destroySlider() {
        if (this.slider) {
            this.sliderContainerDiv.parentNode.removeChild(this.sliderContainerDiv);
            this.slider.container = null;
            this.slider = null;
            this.sliderContainerDiv = null;
        }
        this.classes.nativeElement.disabled = false;
    }

    destroyComponent() {
        if (this.layer && !this.keepThemeOnOriginalLayer) {
            this.removeTheme();
        }
        this.numSelectedClasses = this.defaultNumClasses;
        this.errorMessage = null;
        this.loadingData = false;
        this.colorPickerClassNo = null;
        this.classBreakInfos = [];
        this.currentColors = [];
        this.renderers = [];
        this.symbol = [];
        this.dispatchUpdateEvent();
    }

    themeLayer() {
        this.keepThemeOnOriginalLayer = true;
        this.closeWindow();
    }

    closeWindow() {
        this.classInputSub.unsubscribe();
        if (this.windowRef) {
            this.windowRef.close();
        }
    }

    async deThemeLayer() {
        this.keepThemeOnOriginalLayer = false;
        this.closeWindow();
        this.themeService.forgetTheme(this.layer);
    }

    private async removeTheme() {
        if (this.layer) {

            const [SimpleRenderer] = await Promise.all([ESRI.SimpleRenderer]);
            // First get the renderer definitions for each sublayer
            const rendererDefinitions = [];
            for (let i = 0; i < this.layer.sublayers.length; i++) {
                if (this.layer.mapcoreProps.simpleRenderers) {
                    rendererDefinitions.push(this.layer.mapcoreProps.simpleRenderers[i]);
                } else {
                    const newRendererDefinition = this.themeService.getNewRendererForSublayer(this.layer.sublayers.getItemAt(i));
                    rendererDefinitions.push(newRendererDefinition);
                    if (isSymbolPropertiesWithOutline(newRendererDefinition.symbol)) {
                        newRendererDefinition.symbol.outline.color = newRendererDefinition.symbol.color;
                    }
                }
            }
            delete this.layer.mapcoreProps.simpleRenderers;

            //Then set the new renderer
            for (const { sublayer, index } of this.layer.sublayers.toArray().map((s, i) => ({ sublayer: s, index: i }))) {
                const renderer = rendererDefinitions[index];
                sublayer.renderer = new SimpleRenderer({ symbol: renderer.symbol });
            }
            this.removeFieldFromLayerName(this.layer);
            this.themeService.forgetTheme(this.layer);
            this.layer.mapcoreProps.isThemed = false;
            this.layerService.mapLayersSub.next();
            this.layerService.layersRefreshed.emit();
        }
    }

    async splitLayer() {
        if (this.layerColumns) {
            const field = this.renderers[0].field;
            const column = this.layerColumns.find(x => x.name === field);
            const groupParent = {
                layers: [],
                name: this.layer.mapcoreProps.name,
                checked: true
            };
            const currentQuery = (this.layer.mapcoreProps.query && this.layer.mapcoreProps.query.subqueries) ? this.layer.mapcoreProps.query.subqueries : [this.layer.mapcoreProps.query];
            const firstRenderer = this.renderers[0];
            const infos = isClassBreaksRenderer(firstRenderer) ? firstRenderer.classBreakInfos : firstRenderer.uniqueValueInfos;
            for (let i = 0; i < infos.length; i++) {
                let label = '';
                const subquery = [];
                if (this.layerService.isProperQuery(this.layer.mapcoreProps.query)) {
                    subquery.push(...currentQuery);
                }
                if (isClassBreakInfo(infos)) {
                    label = `${infos[i].minValue}-${infos[i].maxValue}`;
                    const isLastBreak = (i === infos.length - 1);
                    subquery.push(this.buildRangeQuery(infos[i], field, isLastBreak));
                } else {
                    label = infos[i].label;
                    subquery.push(this.buildUniqueQuery(infos[i], field));
                }
                const renderers = this.getSimpleRenderersForClass(i);
                groupParent.layers.push({
                    id: this.layer.mapcoreProps.parentId,
                    visible: true,
                    groupParent,
                    name: `${column && column.alias ? column.alias : field} (${label})`,
                    renderers,
                    query: {
                        operator: 'AND',
                        subqueries: subquery
                    },
                    opacity: this.layer.opacity,
                    parentQuery: this.layer.mapcoreProps.parentQuery
                });

            }
            const layers = await this.layerService.addMapcoreLayersToMap(groupParent.layers);
            const group = layers.find(layer => layer.type === 'group') as IMapcoreGroupLayer;
            group.layers.reverse();
            this.layerService.moveLayerAbove(group, this.layer);
            this.layer.visible = false;
            this.keepThemeOnOriginalLayer = false;
            this.closeWindow();
        }
    }

    buildRangeQuery(classBreak: __esri.ClassBreakInfo, filterField: string, lastBreak: boolean = false) {
        return {
            operator: 'AND',
            subqueries: [
                {
                    field: filterField,
                    operator: '>=',
                    value: classBreak.minValue.toString()
                },
                {
                    field: filterField,
                    operator: lastBreak ? '<=' : '<',
                    value: classBreak.maxValue.toString()
                }
            ]
        };
    }

    buildUniqueQuery(classBreak: __esri.UniqueValueInfo, filterField: string) {
        const val = classBreak.value === null ? 'null' : classBreak.value.toString();
        return {
            operator: 'AND',
            isHiddenFilter: true,
            subqueries: [
                {
                    field: filterField,
                    operator: val.toLowerCase() === 'null' ? 'IS NULL' : '=',
                    value: val.toLowerCase() === 'null' ? null : val
                }
            ]
        };
    }

    private getSimpleRenderersForClass(infoIndex: number): any[] {
        const renderers: any[] = [];
        for (const sublayer of this.layer.sublayers.toArray()) {
            const r = sublayer.renderer;
            if (isClassBreaksRenderer(r)) {
                renderers.push({ type: 'simple', symbol: r.classBreakInfos[infoIndex].symbol, visualVariables: this.layer.mapcoreProps.simpleRenderers[0].visualVariables });
            } else if (isUniqueValueRenderer(r)) {
                renderers.push({ type: 'simple', symbol: r.uniqueValueInfos[infoIndex].symbol, visualVariables: this.layer.mapcoreProps.simpleRenderers[0].visualVariables });
            }
        }
        return renderers;
    }
}

type ValidationState =
    | 'fresh'
    | 'numeric_resetNumSelectedClasses'
    | 'nonNumeric_forcedUniqueValues'
    | 'failed';
